import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import CategoryService from '../../services/category.service';
import ProductService from '../../services/product.service';

const CategoryView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchCategory();
    }
  }, [id]);

  useEffect(() => {
    if (category) {
      fetchProducts();
    }
  }, [category]);

  const fetchCategory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await CategoryService.getOne(Number(id));
      setCategory(response.data?.data);
    } catch (error: any) {
      console.error('Error fetching category:', error);
      setError(error.response?.data?.error?.message || 'Failed to load category');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      
      // Fetch products for this category
      const response = await ProductService.getAll({
        'filters[category][id][$eq]': Number(id),
        populate: ['images', 'category'],
        'pagination[pageSize]': 100,
      });
      
      const directProducts = response.data?.data || [];
      
      // Fetch products from sub-categories if they exist
      let subCategoryProducts: any[] = [];
      if (category?.attributes?.subCategories?.data?.length > 0) {
        const subCategoryIds = category.attributes.subCategories.data.map((sub: any) => sub.id);
        
        // Fetch products from all sub-categories
        const subPromises = subCategoryIds.map((subId: number) =>
          ProductService.getAll({
            'filters[category][id][$eq]': subId,
            populate: ['images', 'category'],
            'pagination[pageSize]': 100,
          })
        );
        
        const subResponses = await Promise.all(subPromises);
        subCategoryProducts = subResponses.flatMap(res => res.data?.data || []);
      }
      
      // Combine and set all products
      setProducts([...directProducts, ...subCategoryProducts]);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await CategoryService.delete(Number(id));
        navigate('/categories');
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !category) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/categories')}
          sx={{ mb: 2 }}
        >
          Back to Categories
        </Button>
        <Alert severity="error">{error || 'Category not found'}</Alert>
      </Box>
    );
  }

  const data = category.attributes;
  const imageUrl = data.image?.data?.attributes?.url
    ? `${(process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '')}${data.image.data.attributes.url}`
    : null;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/categories')}>
            Back
          </Button>
          <CategoryIcon />
          <Typography variant="h4">{data.name}</Typography>
          <Chip
            label={data.isActive ? 'Active' : 'Inactive'}
            color={data.isActive ? 'success' : 'default'}
            size="medium"
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/categories/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      {/* Main Content */}
      <Stack spacing={3}>
        {/* Category Image & Basic Info */}
        <Paper sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            {/* Image */}
            {imageUrl && (
              <Box>
                <Avatar
                  src={imageUrl}
                  alt={data.name}
                  variant="rounded"
                  sx={{ width: 200, height: 200 }}
                >
                  <CategoryIcon sx={{ fontSize: 60 }} />
                </Avatar>
              </Box>
            )}

            {/* Details */}
            <Box flex={1}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Category Name
                  </Typography>
                  <Typography variant="h5">{data.name}</Typography>
                </Box>

                {data.slug && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Slug
                    </Typography>
                    <Typography variant="body1">{data.slug}</Typography>
                  </Box>
                )}

                {data.description && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">{data.description}</Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Sort Order
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={data.sortOrder || 0} size="small" />
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Paper>

        {/* Category Hierarchy */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Parent Category */}
          {data.parentCategory?.data && (
            <Paper sx={{ p: 3, flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Parent Category
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Chip
                label={data.parentCategory.data.attributes?.name}
                onClick={() => navigate(`/categories/${data.parentCategory.data.id}`)}
                clickable
                color="primary"
              />
            </Paper>
          )}

          {/* Sub-Categories */}
          {data.subCategories?.data && data.subCategories.data.length > 0 && (
            <Paper sx={{ p: 3, flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Sub-Categories ({data.subCategories.data.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                {data.subCategories.data.map((subCat: any) => (
                  <ListItem key={subCat.id} disablePadding>
                    <ListItemButton onClick={() => navigate(`/categories/${subCat.id}`)}>
                      <ListItemText
                        primary={subCat.attributes?.name}
                        secondary={subCat.attributes?.description}
                      />
                      <Chip
                        label={subCat.attributes?.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={subCat.attributes?.isActive ? 'success' : 'default'}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </Stack>

        {/* Products in this Category */}
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Products ({products.length})
            </Typography>
            {products.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/products/create')}
              >
                Add Product
              </Button>
            )}
          </Stack>
          <Divider sx={{ mb: 3 }} />

          {productsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : products.length === 0 ? (
            <Box textAlign="center" py={6}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No products in this category yet
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/products/create')}
                sx={{ mt: 2 }}
              >
                Add First Product
              </Button>
            </Box>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={3}>
              {products.map((product) => {
                const attrs = product.attributes;
                const imageUrl = attrs?.images?.data?.[0]?.attributes?.url
                  ? `${(process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '')}${attrs.images.data[0].attributes.url}`
                  : 'https://via.placeholder.com/300x200?text=No+Image';

                return (
                  <Box key={product.id} sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(33.33% - 16px)', lg: 'calc(25% - 18px)' } }}>
                    <Card>
                      <CardActionArea onClick={() => navigate(`/products/${product.id}`)}>
                        <CardMedia
                          component="img"
                          height="180"
                          image={imageUrl}
                          alt={attrs?.name}
                          sx={{ objectFit: 'cover', bgcolor: 'grey.100' }}
                        />
                        <CardContent>
                          <Typography variant="h6" noWrap gutterBottom>
                            {attrs?.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            SKU: {attrs?.sku}
                          </Typography>
                          {attrs?.category?.data && (
                            <Chip
                              label={attrs.category.data.attributes?.name}
                              size="small"
                              variant="outlined"
                              sx={{ mb: 1 }}
                            />
                          )}
                          <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                            <Typography variant="h6" color="primary">
                              {attrs?.currency || 'OMR'} {attrs?.price?.toFixed(2)}
                            </Typography>
                            <Chip
                              label={attrs?.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={attrs?.isActive ? 'success' : 'default'}
                            />
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default CategoryView;

